import { selectTenantName } from "features/user/userSlice";
import { useSelector } from "react-redux";
import { useGetClientsWithConfigsQuery } from "../../../features/clients/clientConfigSlice";
import { useGetLotsQuery } from "../../../features/lots/lotSlice";
import { getClientScopedLots, getLotsWithTenantText } from "../../../features/lots/lotUtil";
import DropdownFilter from "../../DropdownFilter";

// Sentinel id for the opt-in "All {tenant} Lots" convenience option. It is never
// persisted in the selection — selecting it expands to the tenant-owned lot ids
// within the current (client-scoped) list. It's a first-class option in this
// DropdownFilter (native multi-select), not the ListFilter popover's selectAllOptions helper.
export const ALL_TENANT_LOTS_OPTION_ID = "__ALL_TENANT_LOTS__";

export default function LotsDropdown({
    title,
    identifier,
    onSelect,
    showEmpty,
    clientId,
    value,
    multiple,
    ownedByTenant,
    required,
    classStyles,
    newLots = [],
    filteredLots = null,
    withTypeAhead = false,
    allTenantLotsOption = false,
}) {
    const tenantName = useSelector(selectTenantName);

    let { data: clientsWithConfigs, error: error, isLoading: isLoading } = useGetClientsWithConfigsQuery(null);
    let { data: getLots, error: lotsError, isLoading: isLoadingLots } = useGetLotsQuery();
    let lots = getClientScopedLots(getLots, clientsWithConfigs, clientId);

    lots = filteredLots !== null ? clientId > 0 ? filteredLots.filter(lot => lots?.find(l => l.id == lot.id)) : filteredLots : ownedByTenant ? lots?.filter(lot => lot.ownedByTenant) : lots;

    lots = (lots && newLots) ? [...lots, ...newLots] : lots;

    const lotOptions = getLotsWithTenantText(lots, tenantName) ?? [];

    // Opt-in bulk-select for tenant-owned lots. Off by default so the many other
    // consumers of this shared dropdown are unaffected. Only shown when at least one
    // tenant-owned lot is present in the current (client-scoped) list.
    const showAllTenantLots = allTenantLotsOption && multiple && lots?.some(lot => lot.ownedByTenant);
    const allTenantLotsLabel = tenantName ? `All ${tenantName} Lots` : "All Tenant Lots";
    const options = showAllTenantLots
        ? [{ id: ALL_TENANT_LOTS_OPTION_ID, name: allTenantLotsLabel }, ...lotOptions]
        : lotOptions;

    const handleSelect = (selected, id) => {
        if (!showAllTenantLots) {
            onSelect(selected, id);
            return;
        }

        const hasSentinel = Array.isArray(selected) && selected.includes(ALL_TENANT_LOTS_OPTION_ID);
        if (!hasSentinel) {
            // The sentinel isn't in the new selection — either "All" was toggled off or
            // the user is picking individual lots. Pass the plain lot ids through.
            onSelect(selected, id);
            return;
        }

        const hadSentinel = Array.isArray(value) && value.includes(ALL_TENANT_LOTS_OPTION_ID);
        if (hadSentinel) {
            // "All {tenant} Lots" was already active and the user picked another lot —
            // cancel "All" and keep only the newly-toggled lot(s).
            onSelect(selected.filter(v => v !== ALL_TENANT_LOTS_OPTION_ID), id);
            return;
        }

        // Just switched "All {tenant} Lots" on — collapse to the single sentinel so the
        // field shows one chip instead of every individual lot. The consuming view
        // resolves the sentinel to the tenant-owned lot ids for columns/data.
        onSelect([ALL_TENANT_LOTS_OPTION_ID], id);
    };

    return (
        <DropdownFilter
            title={title}
            identifier={identifier}
            values={options}
            onSelect={showAllTenantLots ? handleSelect : onSelect}
            value={value}
            showEmpty={showEmpty}
            multiple={multiple}
            required={required}
            classStyles={classStyles}
            withTypeAhead={withTypeAhead}
        />
    )
}